import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ArrowRight, Loader2, Mail, User, Lock, Sun, Moon } from 'lucide-react';
import api from '../api/axios';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const CandidateAuth = () => {
  const navigate = useNavigate();
  
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // --- Dynamic Theme Logic ---
  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') !== 'light');
  useEffect(() => {
    if (isDark) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); } 
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDark]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsLoading(true);
    
    try {
      let response;
      if (isLoginMode) {
        response = await api.post('/candidate/login', { email, password }); 
      } else {
        response = await api.post('/candidate/register', { email, name, password });
      }
      
      localStorage.setItem('candidate_email', response.data.email || email);
      localStorage.setItem('candidate_name', response.data.name || name);
      
      navigate('/candidate-portal');
    } catch (error) {
      console.error("Auth Error", error);
      setErrorMsg(error.response?.data?.detail || "Authentication failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLoginMode(!isLoginMode);
    setErrorMsg('');
    setPassword('');
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex items-center justify-center relative overflow-hidden overflow-x-hidden transition-colors duration-500 px-4 py-8">
      <style>{customStyles}</style>

      {/* Theme Toggle Button - Floating Top Right */}
      <div className="absolute top-6 right-6 z-50">
        <button 
          onClick={() => setIsDark(!isDark)} 
          className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors duration-300 focus:outline-none ${isDark ? 'bg-indigo-500/20 border border-indigo-500/30' : 'bg-slate-200 border border-slate-300 shadow-sm'}`}
          title="Toggle Theme"
        >
          <span className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full transition-transform duration-300 ${isDark ? 'translate-x-7 bg-indigo-400' : 'translate-x-1 bg-white shadow-sm'}`}>
            {isDark ? <Moon size={10} className="text-[#0a0a0a]" /> : <Sun size={10} className="text-slate-400" />}
          </span>
        </button>
      </div>

      {/* Ambient Glows */}
      <div className="absolute top-[10%] left-[20%] w-[40%] h-[40%] rounded-full bg-indigo-200/40 dark:bg-indigo-600/10 blur-[120px] animate-blob z-0 pointer-events-none"></div>
      <div className="absolute bottom-[10%] right-[20%] w-[40%] h-[40%] rounded-full bg-violet-200/40 dark:bg-violet-600/10 blur-[120px] animate-blob z-0 pointer-events-none" style={{ animationDelay: '2s' }}></div>

      <div className="w-full max-w-md relative z-10 animate-in fade-in zoom-in-95 duration-500">
        
        {/* Brand Header */}
        <div className="flex flex-col items-center justify-center mb-8 sm:mb-10">
          <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-[1.5rem] flex items-center justify-center mb-6 shadow-md dark:shadow-2xl backdrop-blur-md">
            <Briefcase size={28} className="text-indigo-600 dark:text-indigo-400" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-wide text-slate-900 dark:text-white text-center transition-colors">Candidate Portal</h1>
          <p className="text-slate-500 text-[12px] sm:text-xs tracking-wide font-bold mt-3 text-center">Access Your Interviews</p>
        </div>

        {/* Auth Card */}
        <form onSubmit={handleSubmit} className="bg-white/80 dark:bg-[#0a0a0a]/50 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-6 sm:p-10 rounded-[2rem] shadow-xl dark:shadow-2xl flex flex-col gap-6">
          
          <div className="space-y-5">
            {!isLoginMode && (
              <div className="animate-in slide-in-from-top-2 fade-in">
                <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-2 flex items-center gap-2">
                  <User size={14} className="text-violet-500 dark:text-violet-400"/> Full Name
                </label>
                <input 
                  type="text" required disabled={isLoading}
                  placeholder="e.g. Somya Parihar" value={name} onChange={e => setName(e.target.value)}
                  className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl px-4 py-4 sm:px-5 sm:py-4 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                />
              </div>
            )}

            <div>
              <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-2 flex items-center gap-2">
                <Mail size={14} className="text-indigo-500 dark:text-indigo-400"/> Email Address
              </label>
              <input 
                type="email" required disabled={isLoading}
                placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl px-4 py-4 sm:px-5 sm:py-4 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-2 flex items-center gap-2">
                <Lock size={14} className="text-indigo-500 dark:text-indigo-400"/> Password
              </label>
              <input 
                type="password" required disabled={isLoading}
                placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-2xl px-4 py-4 sm:px-5 sm:py-4 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
              />
            </div>

            {errorMsg && (
              <div className="text-rose-600 dark:text-rose-500 text-xs font-medium bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 p-3 rounded-xl">
                {errorMsg}
              </div>
            )}
          </div>

          <button 
            type="submit" disabled={isLoading || !email || !password || (!isLoginMode && !name)}
            className="w-full bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white rounded-2xl py-4 text-xs font-bold tracking-wide transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 mt-2"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin"/> : (isLoginMode ? 'Sign In Securely' : 'Create Account')}
            {!isLoading && <ArrowRight size={16} strokeWidth={2}/>}
          </button>
        </form>

        <div className="mt-8 text-center">
          <button 
            onClick={toggleMode} disabled={isLoading}
            className="text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-white text-[12px] sm:text-xs font-medium tracking-wide transition-colors disabled:opacity-50 word-spacing-[0.05em]"
          >
            {isLoginMode ? "Don't have an account? Register" : "Already have an account? Sign in"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CandidateAuth;